import React, {
  useState, useEffect, useRef, useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import {
  Box, IconButton, Typography, TextField, Chip,
  CircularProgress, Collapse, Tooltip, Badge, GlobalStyles,
} from "@mui/material";
import {
  X, RotateCcw, Send, ChevronDown,
} from "lucide-react";
import { fetchKbs } from "../../components/pages/ai/knowledgeSlice";
import { fetchAiSettings, fetchAiWallet } from "../../components/pages/ai/aiUsageSlice";
import axiosInstance from "../../services/axiosInstance";

import teliWaveImg from "../../../design-system/assets/mascot/teli-wave.png";
import glyphImg    from "../../../design-system/assets/brand/glyph.png";

// ── Constants ────────────────────────────────────────────────────────────────
const SESSION_TIMEOUT_MS  = 15 * 60 * 1000; // 15 min
const MAX_USER_MESSAGES   = 50;
const MAX_INPUT_CHARS     = 500;
const CHAR_WARN_RATIO     = 0.7;

const SS_CHAT  = "teliWidget.chat";
const SS_CONV  = "teliWidget.convId";
const SS_KB    = "teliWidget.kbId";

function tryRead(key)        { try { return sessionStorage.getItem(key); } catch { return null; } }
function tryWrite(key, val)  { try { sessionStorage.setItem(key, val);   } catch { /* noop */ } }
function tryRemove(key)      { try { sessionStorage.removeItem(key);      } catch { /* noop */ } }

function restoreMessages() {
  try {
    const raw = sessionStorage.getItem(SS_CHAT);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function persistMessages(msgs) {
  try { sessionStorage.setItem(SS_CHAT, JSON.stringify(msgs)); } catch { /* noop */ }
}

// ── WHERE TELI SITS (the owner, 2026-11-02: "the mascot is over the Disconnect button — make it adjustable") ─────────────────
// Dragged anywhere, and kept there in this browser (a per-viewer convenience): how far it moved from its corner, toward the
// inline start and upward — logical, so a move made in English stays sensible in Arabic. Held inside the screen on every resize.
const LS_POS = "teliWidget.offset";
const FAB = 60;        // the button's size
const EDGE = 24;       // its corner gap
const MARGIN = 8;      // the nearest it comes to the screen's edge
const DRAG_PX = 6;     // a press that moves less than this is a click
function readOffset() {
  try {
    const v = JSON.parse(localStorage.getItem(LS_POS) || "null");
    return v && Number.isFinite(v.inline) && Number.isFinite(v.block) ? v : { inline: 0, block: 0 };
  } catch { return { inline: 0, block: 0 }; }
}
function writeOffset(v) { try { localStorage.setItem(LS_POS, JSON.stringify(v)); } catch { /* per visit */ } }
function dockWidth() {
  try { return parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--phone-dock-w")) || 0; } catch { return 0; }
}
function clampOffset({ inline, block }) {
  if (typeof window === "undefined") return { inline, block };
  const maxInline = Math.max(0, window.innerWidth - FAB - EDGE - MARGIN - dockWidth());
  const maxBlock = Math.max(0, window.innerHeight - FAB - EDGE - MARGIN);
  return {
    inline: Math.round(Math.min(Math.max(inline, MARGIN - EDGE), maxInline)),
    block: Math.round(Math.min(Math.max(block, MARGIN - EDGE), maxBlock)),
  };
}

// ── ChatWidget ───────────────────────────────────────────────────────────────
export default function ChatWidget() {
  const { t, i18n } = useTranslation("widget");
  const dispatch = useDispatch();

  // Redux state
  const kbs           = useSelector((s) => s.knowledge?.kbs ?? []);
  const kbsStatus     = useSelector((s) => s.knowledge?.kbsStatus ?? "idle");
  const settings      = useSelector((s) => s.aiUsage?.settings);
  const settingsStatus= useSelector((s) => s.aiUsage?.settingsStatus ?? "idle");
  const wallet        = useSelector((s) => s.aiUsage?.wallet);
  const walletStatus  = useSelector((s) => s.aiUsage?.walletStatus ?? "idle");

  const ragEnabled = settings !== null
    ? (settings?.ragEnabled ?? settings?.aiKbEnabled ?? settings?.rag ?? false)
    : true; // optimistic until loaded

  // Tokens are the tenant unit; a balance under one chat reply's worth of tokens is "low".
  const balance    = wallet?.tokens?.total ?? (wallet?.balance != null ? Number(wallet.balance) / (wallet.tokenUsd ?? 0.001) : null);
  const isLowWallet = balance !== null && balance < 1000;

  const activeKbs  = kbs.filter((k) => k.status === "active" || k.status === "ready" || !k.status);

  // Local state
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState(() => restoreMessages());
  const [convId, setConvId]     = useState(() => tryRead(SS_CONV) || null);
  const [selectedKbId, setSelectedKbId] = useState(() => tryRead(SS_KB) || null);
  const [input, setInput]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [unread, setUnread]     = useState(0);

  // Where Teli was dragged to, and the drag in progress.
  const [offset, setOffset] = useState(() => clampOffset(readOffset()));
  const offsetRef = useRef(offset);
  const dragRef = useRef(null);            // { x, y, start, moved }
  const draggedRef = useRef(false);        // the click that ends a drag opens nothing
  const moveTo = useCallback((next) => { offsetRef.current = next; setOffset(next); }, []);
  useEffect(() => {
    const onResize = () => moveTo(clampOffset(offsetRef.current));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [moveTo]);

  const lastActivityRef = useRef(Date.now());
  const messagesEndRef  = useRef(null);
  const inputRef        = useRef(null);
  const timerRef        = useRef(null);

  // ── Bootstrap data ───────────────────────────────────────────────────────
  useEffect(() => {
    if (kbsStatus === "idle")       dispatch(fetchKbs());
    if (settingsStatus === "idle")  dispatch(fetchAiSettings());
    if (walletStatus === "idle")    dispatch(fetchAiWallet());
  }, [dispatch, kbsStatus, settingsStatus, walletStatus]);

  // Auto-select first active KB when list loads
  useEffect(() => {
    if (!selectedKbId && activeKbs.length > 0) {
      const id = String(activeKbs[0].id);
      setSelectedKbId(id);
      tryWrite(SS_KB, id);
    }
  }, [activeKbs, selectedKbId]);

  // Persist messages
  useEffect(() => { persistMessages(messages); }, [messages]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Unread badge when closed
  useEffect(() => {
    if (!open && messages.length > 0) {
      const last = messages[messages.length - 1];
      if (last?.role === "assistant") setUnread((n) => n + 1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // ── Session timeout watchdog ─────────────────────────────────────────────
  const expireSession = useCallback(() => {
    const sys = { role: "system", text: t("sessionExpired", { min: 15 }), id: Date.now() };
    setMessages((prev) => [...prev, sys]);
    setConvId(null);
    tryRemove(SS_CONV);
  }, [t]);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      if (Date.now() - lastActivityRef.current >= SESSION_TIMEOUT_MS) {
        clearInterval(timerRef.current);
        expireSession();
      }
    }, 60_000);
    return () => clearInterval(timerRef.current);
  }, [expireSession]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const resetActivity = () => { lastActivityRef.current = Date.now(); };

  const handleOpen = () => {
    setOpen(true);
    setUnread(0);
    resetActivity();
    setTimeout(() => inputRef.current?.focus(), 120);
  };

  const handleClose = () => setOpen(false);

  const handleNewChat = () => {
    setMessages([]);
    setConvId(null);
    tryRemove(SS_CONV);
    tryRemove(SS_CHAT);
    resetActivity();
    setTimeout(() => inputRef.current?.focus(), 80);
  };

  const handleKbSelect = (id) => {
    setSelectedKbId(id);
    tryWrite(SS_KB, id);
  };

  const userMsgCount = messages.filter((m) => m.role === "user").length;
  const atLimit      = userMsgCount >= MAX_USER_MESSAGES;
  const noKb         = activeKbs.length === 0;
  const canSend      = !loading && !atLimit && !noKb && input.trim().length > 0
                       && input.length <= MAX_INPUT_CHARS && ragEnabled;

  const handleSend = async () => {
    if (!canSend) return;
    const text = input.trim();
    resetActivity();

    const userMsg = { role: "user", text, id: Date.now() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const body = {
        message: text,
        knowledgeBaseIds: selectedKbId ? [selectedKbId] : activeKbs.map((k) => String(k.id)),
        ...(convId ? { conversationId: convId } : {}),
      };
      const res = await axiosInstance.post("/ai/chat", body);
      const data = res.data;

      const newConv = data.conversationId || data.convId || convId;
      if (newConv) { setConvId(newConv); tryWrite(SS_CONV, newConv); }

      const citations = Array.isArray(data.citations)
        ? data.citations.filter(Boolean)
        : [];

      const assistantMsg = {
        role:      "assistant",
        text:      data.answer || data.message || "",
        citations,
        id:        Date.now() + 1,
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      const errMsg = {
        role: "assistant",
        text: err?.response?.data?.error || t("errorDefault"),
        isError: true,
        id: Date.now() + 1,
      };
      setMessages((prev) => [...prev, errMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Hidden if settings loaded and RAG explicitly off
  if (settings !== null && !ragEnabled) return null;

  const showCharCount = input.length > MAX_INPUT_CHARS * CHAR_WARN_RATIO;

  return (
    <>
      <GlobalStyles styles={`
        @keyframes teliFloat {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-8px); }
        }
        @keyframes widgetSlideIn {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .teli-float { animation: none !important; }
          .widget-panel { animation: none !important; }
        }
      `} />

      {/* ── Panel ─────────────────────────────────────────────────────── */}
      {open && (
        <Box
          className="widget-panel"
          role="dialog"
          aria-label={t("title")}
          sx={{
            position: "fixed",
            // Above wherever Teli was dragged to, and always on screen.
            bottom: `clamp(${MARGIN}px, ${88 + offset.block}px, calc(100vh - min(520px, 100vh - 16px) - ${MARGIN}px))`,
            // Beside the docked phone, never over it (4.8f.4b: PhoneProvider sets --phone-dock-w; 0px when minimised or none).
            insetInlineEnd: `clamp(${MARGIN}px, calc(${EDGE + offset.inline}px + var(--phone-dock-w, 0px)), calc(100vw - min(380px, 100vw - 32px) - ${MARGIN}px))`,
            width: { xs: "calc(100vw - 32px)", sm: "380px" },
            maxHeight: "520px",
            display: "flex",
            flexDirection: "column",
            borderRadius: "var(--r-xl, 20px)",
            boxShadow: "var(--shadow-lg, 0 20px 60px rgba(0,0,0,.18))",
            overflow: "hidden",
            zIndex: 1300,
            background: "var(--surface-card, #fff)",
            animation: "widgetSlideIn 200ms var(--ease-soft, cubic-bezier(.4,0,.2,1)) both",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: "var(--grad-brand-135, linear-gradient(135deg,#7A3FE4 0%,#0E9E8E 100%))",
              px: "16px",
              py: "12px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={glyphImg}
              alt=""
              aria-hidden="true"
              sx={{ width: 32, height: 32, objectFit: "contain", flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px", lineHeight: 1.2, fontFamily: "var(--font-display)" }}>
                {t("title")}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,.75)", fontSize: "12px", fontFamily: "var(--font-ui)" }}>
                {t("subtitle")}
              </Typography>
            </Box>
            <Tooltip title={t("newChat")}>
              <IconButton
                size="small"
                onClick={handleNewChat}
                aria-label={t("newChat")}
                sx={{ color: "rgba(255,255,255,.85)", "&:hover": { background: "rgba(255,255,255,.15)" } }}
              >
                <RotateCcw size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("closeLabel")}>
              <IconButton
                size="small"
                onClick={handleClose}
                aria-label={t("closeLabel")}
                sx={{ color: "rgba(255,255,255,.85)", "&:hover": { background: "rgba(255,255,255,.15)" } }}
              >
                <X size={16} />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Low-wallet warning */}
          {isLowWallet && (
            <Box sx={{
              background: "#FEF3C7", color: "#92400E",
              px: "14px", py: "6px",
              fontSize: "12px", fontFamily: "var(--font-ui)",
              flexShrink: 0,
            }}>
              {t("lowWallet")}
            </Box>
          )}

          {/* KB selector — only when >1 active KB */}
          {activeKbs.length > 1 && (
            <Box sx={{
              px: "12px", pt: "10px", pb: "4px",
              display: "flex", gap: "6px", flexWrap: "wrap",
              borderBottom: "1px solid var(--ink-100, #F1F5F9)",
              flexShrink: 0,
            }}>
              {activeKbs.map((kb) => (
                <Chip
                  key={kb.id}
                  label={kb.name}
                  size="small"
                  onClick={() => handleKbSelect(String(kb.id))}
                  sx={{
                    fontSize: "11px",
                    height: "24px",
                    cursor: "pointer",
                    background: String(kb.id) === selectedKbId
                      ? "var(--brand-violet, #7A3FE4)"
                      : "var(--ink-100, #F1F5F9)",
                    color: String(kb.id) === selectedKbId ? "#fff" : "var(--ink-600, #475569)",
                    fontWeight: String(kb.id) === selectedKbId ? 600 : 400,
                    "&:hover": { opacity: 0.85 },
                  }}
                />
              ))}
            </Box>
          )}

          {/* Messages area */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              px: "14px",
              py: "12px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              minHeight: 0,
            }}
          >
            {/* Empty / welcome state */}
            {messages.length === 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, gap: "12px", py: "24px" }}>
                <Box
                  component="img"
                  src={teliWaveImg}
                  alt=""
                  aria-hidden="true"
                  sx={{ height: 80, width: "auto", objectFit: "contain" }}
                />
                {kbsStatus === "loading" ? (
                  <Typography sx={{ fontSize: "13px", color: "var(--ink-400)", fontFamily: "var(--font-ui)", textAlign: "center" }}>
                    {t("kbLoading")}
                  </Typography>
                ) : noKb ? (
                  <Typography sx={{ fontSize: "13px", color: "var(--ink-400)", fontFamily: "var(--font-ui)", textAlign: "center", maxWidth: 260 }}>
                    {t("noKb")}
                  </Typography>
                ) : (
                  <Typography sx={{ fontSize: "13px", color: "var(--ink-500)", fontFamily: "var(--font-ui)", textAlign: "center", maxWidth: 280, lineHeight: 1.5 }}>
                    {t("welcome")}
                  </Typography>
                )}
              </Box>
            )}

            {/* Message list */}
            {messages.map((msg) => {
              if (msg.role === "system") {
                return (
                  <Box key={msg.id} sx={{ display: "flex", justifyContent: "center" }}>
                    <Typography sx={{
                      fontSize: "11px", color: "var(--ink-400)",
                      fontFamily: "var(--font-ui)", textAlign: "center",
                      background: "var(--ink-50, #F8FAFC)",
                      px: "10px", py: "4px",
                      borderRadius: "100px",
                      maxWidth: "90%",
                    }}>
                      {msg.text}
                    </Typography>
                  </Box>
                );
              }

              const isUser = msg.role === "user";
              return (
                <Box key={msg.id} sx={{ display: "flex", flexDirection: "column", alignItems: isUser ? "flex-end" : "flex-start" }}>
                  <Box sx={{
                    maxWidth: "82%",
                    px: "12px",
                    py: "8px",
                    borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
                    background: isUser
                      ? "var(--brand-violet, #7A3FE4)"
                      : msg.isError
                        ? "#FEF2F2"
                        : "var(--ink-50, #F8FAFC)",
                    color: isUser ? "#fff" : msg.isError ? "#B91C1C" : "var(--ink-800, #1E293B)",
                    fontSize: "13.5px",
                    lineHeight: 1.55,
                    fontFamily: "var(--font-ui)",
                    wordBreak: "break-word",
                  }}>
                    {msg.text}
                  </Box>
                  {/* Citations */}
                  {!isUser && msg.citations?.length > 0 && (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", mt: "4px", maxWidth: "82%" }}>
                      {msg.citations.slice(0, 3).map((c, i) => (
                        <Chip
                          key={i}
                          label={`${t("sourceLabel")}: ${c.sourceName || c.source || c.chunkId || i + 1}`}
                          size="small"
                          sx={{
                            fontSize: "10px", height: "20px",
                            background: "var(--ink-100, #F1F5F9)",
                            color: "var(--ink-500)",
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}

            {/* Typing indicator */}
            {loading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <CircularProgress size={14} sx={{ color: "var(--brand-violet, #7A3FE4)" }} />
                <Typography sx={{ fontSize: "12px", color: "var(--ink-400)", fontFamily: "var(--font-ui)", fontStyle: "italic" }}>
                  {t("typing")}
                </Typography>
              </Box>
            )}

            {/* Limit reached */}
            {atLimit && (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Typography sx={{
                  fontSize: "11px", color: "var(--ink-400)",
                  textAlign: "center", fontFamily: "var(--font-ui)",
                  background: "var(--ink-50, #F8FAFC)",
                  px: "10px", py: "4px", borderRadius: "100px", maxWidth: "90%",
                }}>
                  {t("limitReached", { max: MAX_USER_MESSAGES })}
                </Typography>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Input row */}
          <Box sx={{
            px: "12px", pt: "8px", pb: "6px",
            borderTop: "1px solid var(--ink-100, #F1F5F9)",
            display: "flex",
            alignItems: "flex-end",
            gap: "8px",
            flexShrink: 0,
          }}>
            <TextField
              inputRef={inputRef}
              multiline
              maxRows={3}
              fullWidth
              size="small"
              value={input}
              onChange={(e) => { setInput(e.target.value); resetActivity(); }}
              onKeyDown={handleKeyDown}
              placeholder={noKb ? t("noKb") : atLimit ? "" : t("placeholder")}
              disabled={loading || atLimit || noKb || !ragEnabled}
              inputProps={{ maxLength: MAX_INPUT_CHARS + 1, "aria-label": t("placeholder") }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  fontSize: "13.5px",
                  fontFamily: "var(--font-ui)",
                  background: "var(--surface-card, #fff)",
                  "& fieldset": { borderColor: "var(--ink-200, #E2E8F0)" },
                  "&:hover fieldset": { borderColor: "var(--brand-violet, #7A3FE4)" },
                  "&.Mui-focused fieldset": { borderColor: "var(--brand-violet, #7A3FE4)" },
                },
              }}
            />
            <IconButton
              onClick={handleSend}
              disabled={!canSend}
              aria-label={t("send")}
              sx={{
                width: 38, height: 38, flexShrink: 0,
                background: canSend
                  ? "var(--grad-brand-135, linear-gradient(135deg,#7A3FE4 0%,#0E9E8E 100%))"
                  : "var(--ink-100, #F1F5F9)",
                color: canSend ? "#fff" : "var(--ink-300)",
                borderRadius: "10px",
                transition: "background 200ms",
                "&:hover": { opacity: 0.9 },
                "&.Mui-disabled": {
                  background: "var(--ink-100, #F1F5F9)",
                  color: "var(--ink-300)",
                },
              }}
            >
              <Send size={16} />
            </IconButton>
          </Box>

          {/* Footer */}
          <Box sx={{
            px: "14px", pb: "8px",
            display: "flex",
            alignItems: "center",
            justifyContent: showCharCount ? "space-between" : "center",
            flexShrink: 0,
          }}>
            {showCharCount && (
              <Typography sx={{
                fontSize: "10px",
                color: input.length > MAX_INPUT_CHARS ? "#EF4444" : "var(--ink-400)",
                fontFamily: "var(--font-ui)",
              }}>
                {t("charCount", { count: input.length, max: MAX_INPUT_CHARS })}
              </Typography>
            )}
            <Typography sx={{ fontSize: "10px", color: "var(--ink-300)", fontFamily: "var(--font-ui)" }}>
              {t("dataPrivacy")}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── FAB trigger ──────────────────────────────────────────────────── */}
      <Badge
        badgeContent={!open ? unread : 0}
        max={9}
        sx={{
          position: "fixed",
          bottom: `${EDGE + offset.block}px`,
          insetInlineEnd: `calc(${EDGE + offset.inline}px + var(--phone-dock-w, 0px))`,
          zIndex: 1301,
          "& .MuiBadge-badge": {
            background: "var(--brand-violet, #7A3FE4)",
            color: "#fff",
            fontWeight: 700,
            fontSize: "10px",
            minWidth: "18px",
            height: "18px",
            borderRadius: "9px",
          },
        }}
      >
        <Box
          component="button"
          onClick={() => {
            if (draggedRef.current) { draggedRef.current = false; return; }
            (open ? handleClose : handleOpen)();
          }}
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            dragRef.current = { x: e.clientX, y: e.clientY, start: offsetRef.current, moved: false };
            e.currentTarget.setPointerCapture?.(e.pointerId);
          }}
          onPointerMove={(e) => {
            const d = dragRef.current;
            if (!d) return;
            const dx = e.clientX - d.x;
            const dy = e.clientY - d.y;
            if (!d.moved && Math.hypot(dx, dy) < DRAG_PX) return;
            d.moved = true;
            // Toward the inline start is left in English, right in Arabic; up is up.
            moveTo(clampOffset({ inline: d.start.inline + (i18n.dir() === "rtl" ? dx : -dx), block: d.start.block - dy }));
          }}
          onPointerUp={(e) => {
            const d = dragRef.current;
            dragRef.current = null;
            e.currentTarget.releasePointerCapture?.(e.pointerId);
            if (d?.moved) {
              draggedRef.current = true;
              writeOffset(offsetRef.current);
              // The click that follows this release is skipped; a browser that sends none must not eat the next real click.
              setTimeout(() => { draggedRef.current = false; }, 0);
            }
          }}
          onPointerCancel={() => { dragRef.current = null; }}
          aria-label={open ? t("closeLabel") : t("openLabel")}
          aria-expanded={open}
          title={`${open ? t("closeLabel") : t("openLabel")} · ${t("dragHint")}`}
          sx={{
            width: 60,
            height: 60,
            borderRadius: "50%",
            border: "none",
            padding: 0,
            cursor: "grab",
            touchAction: "none",
            "&:active": { cursor: "grabbing" },
            background: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "transform 200ms var(--ease-soft)",
            "&:hover": { transform: "scale(1.06)" },
            "&:focus-visible": {
              outline: "3px solid var(--brand-violet, #7A3FE4)",
              outlineOffset: "2px",
            },
          }}
        >
          <Box
            component="img"
            src={teliWaveImg}
            alt=""
            aria-hidden="true"
            className={open ? undefined : "teli-float"}
            sx={{
              height: 60,
              width: "auto",
              objectFit: "contain",
              filter: "drop-shadow(0 8px 18px rgba(46,55,120,.22))",
              transition: "opacity 200ms",
              opacity: open ? 0.7 : 1,
              animation: open ? "none" : "teliFloat var(--float-loop, 6s) ease-in-out infinite",
            }}
          />
        </Box>
      </Badge>
    </>
  );
}
